import { z } from 'zod';
/**
 * Registro de clientes de Provalia.
 *
 * Dos niveles que no son lo mismo y no deben mezclarse nunca (mismo modelo que
 * el MRP y el Terminal de Producción de ECINSA):
 *
 *   Cliente (tenant) — a DÓNDE van las peticiones: un túnel del hub y un
 *                      Service Layer. Da nombre al subdominio
 *                      <slug>.provalia.ecinsapp.es
 *   Sociedad         — de QUIÉN son los datos: una company_db con su CIF.
 *
 * El CIF vive en la sociedad, jamás en el cliente.
 */
/**
 * Lista de RESERVA de túneles, para cuando el hub no contesta. La buena la da
 * el hub (`GET /api/vpn/registro`). Las mayúsculas son EXACTAS: el nombre
 * viaja tal cual en `X-Ecinsa-Cliente`.
 */
export const HUB_CLIENTS = ['Demo', 'lafermu', 'Selectra', 'Promael', 'MMConecta'];
/** Estado comercial del cliente. Un cliente suspendido no puede entrar en su portal. */
export const SUBSCRIPTION_STATUSES = ['prueba', 'activo', 'suspendido'];
// ── Usuarios del portal de un cliente ─────────────────────────────────────
/**
 * Perfiles dentro del portal de un cliente.
 *
 *   cliente_admin — configura su portal: tolerancias, avisos, proveedores y usuarios
 *   compras       — técnico de compras: ve todo y aprueba lo que sale de tolerancia
 *   recepcion     — verificador de la playa: confirma la llegada física
 *   proveedor     — ve solo lo suyo (su CardCode)
 */
export const TENANT_ROLES = ['cliente_admin', 'compras', 'recepcion', 'proveedor'];
export const ROLE_LABELS = {
    cliente_admin: 'Administrador del cliente',
    compras: 'Técnico de compras',
    recepcion: 'Verificador de recepción',
    proveedor: 'Proveedor',
};
export const DEFAULT_SETTINGS = {
    priceTolerancePct: 5,
    dateToleranceDays: 5,
    evaluationWindowMonths: 6,
    evaluationWeights: { quality: 35, punctuality: 35, availability: 15, agility: 15 },
    reminderDays: [3, 5],
    weeklyDigest: { enabled: true, weekday: 1, hour: 7 },
    buyerEmails: [],
    notifications: {
        newOrder: true,
        reminders: true,
        orderModified: true,
        orderRejected: true,
        dateOutOfTolerance: true,
        priceOutOfTolerance: true,
    },
    smtp: { host: '', port: 587, secure: false, username: '', from: '', hasPassword: false },
    drawings: { basePath: '', pattern: '{item}/{version}/{item}' },
};
// ── Esquemas de entrada ───────────────────────────────────────────────────
const slugPattern = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;
const RESERVED_SLUGS = new Set(['admin', 'www', 'api', 'auth', 'mail', 'static', 'app']);
export const tenantInputSchema = z.object({
    slug: z
        .string()
        .regex(slugPattern, 'Solo minúsculas, dígitos y guiones; es el subdominio del portal')
        .refine((s) => !RESERVED_SLUGS.has(s), { message: 'Ese identificador está reservado' }),
    name: z.string().min(1).max(120),
    hubClient: z.string().min(1).max(60),
    slBaseUrl: z
        .string()
        .url('Debe ser una URL completa')
        .refine((v) => /\/b1s\/v\d+$/.test(v.replace(/\/$/, '')), {
        message: 'Debe terminar en /b1s/v1 o /b1s/v2',
    })
        .refine((v) => !/^https?:\/\/(192\.168\.|172\.(1[6-9]|2\d|3[01])\.|10\.(?!30\.0\.))/.test(v), {
        message: 'Parece la IP de la LAN del cliente. Se accede por su IP dentro de la VPN del hub (10.30.0.X)',
    }),
    slVerifySsl: z.boolean().default(false),
    slTimeoutMs: z.number().int().min(1000).max(120_000).default(30_000),
    active: z.boolean().default(true),
});
export const subscriptionSchema = z.object({
    status: z.enum(SUBSCRIPTION_STATUSES),
    monthlyFee: z.number().min(0).max(100_000).nullable().default(null),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
    commitmentMonths: z.number().int().min(0).max(120).nullable().default(null),
    notes: z.string().max(2000).default(''),
});
export const brandingSchema = z.object({
    displayName: z.string().min(1).max(120),
    accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Color en formato #rrggbb'),
});
export const companyInputSchema = z.object({
    companyDb: z.string().min(1).max(80),
    name: z.string().min(1).max(120),
    slUsername: z.string().min(1).max(80),
    /** Vacío al editar = se conserva la contraseña guardada. */
    slPassword: z.string().max(200).optional().default(''),
    /** Normalmente VACÍO: el CIF se lee del propio SAP. Ver docs/CIF.md. */
    cifOverride: z.string().max(40).optional().default(''),
    isDefault: z.boolean().default(false),
    active: z.boolean().default(true),
});
const emailSchema = z.string().trim().toLowerCase().email('Correo no válido').max(200);
export const portalUserInputSchema = z
    .object({
    email: emailSchema,
    name: z.string().trim().min(1).max(120),
    role: z.enum(TENANT_ROLES),
    cardCode: z.string().trim().max(50).nullable().default(null),
    active: z.boolean().default(true),
    /**
     * Vacía = se le manda por correo un enlace para que cree la suya (lo
     * normal). Solo se escribe aquí cuando no se puede mandar correo.
     */
    password: z.string().max(200).optional().default(''),
})
    .refine((u) => u.role !== 'proveedor' || (u.cardCode !== null && u.cardCode !== ''), {
    message: 'Un usuario proveedor necesita el código de interlocutor (CardCode) de SAP',
    path: ['cardCode'],
});
const weightsSchema = z
    .object({
    quality: z.number().min(0).max(100),
    punctuality: z.number().min(0).max(100),
    availability: z.number().min(0).max(100),
    agility: z.number().min(0).max(100),
})
    .refine((w) => Math.abs(w.quality + w.punctuality + w.availability + w.agility - 100) < 0.001, {
    message: 'Los pesos de la evaluación deben sumar 100',
});
export const settingsInputSchema = z.object({
    priceTolerancePct: z.number().min(0).max(100),
    dateToleranceDays: z.number().int().min(0).max(365),
    evaluationWindowMonths: z.number().int().min(1).max(60),
    evaluationWeights: weightsSchema,
    reminderDays: z.array(z.number().int().min(1).max(60)).max(5),
    weeklyDigest: z.object({
        enabled: z.boolean(),
        weekday: z.number().int().min(0).max(6),
        hour: z.number().int().min(0).max(23),
    }),
    buyerEmails: z.array(emailSchema).max(20),
    notifications: z.object({
        newOrder: z.boolean(),
        reminders: z.boolean(),
        orderModified: z.boolean(),
        orderRejected: z.boolean(),
        dateOutOfTolerance: z.boolean(),
        priceOutOfTolerance: z.boolean(),
    }),
    smtp: z.object({
        host: z.string().max(200),
        port: z.number().int().min(1).max(65535),
        secure: z.boolean(),
        username: z.string().max(200),
        from: z.string().max(200),
        /** Vacía = se conserva la guardada. */
        password: z.string().max(500).optional().default(''),
    }),
    drawings: z.object({
        basePath: z.string().max(500),
        pattern: z
            .string()
            .min(1)
            .max(200)
            .refine((p) => p.includes('{item}') && p.includes('{version}'), {
            message: 'El patrón debe incluir {item} y {version}',
        })
            .refine((p) => !p.includes('..'), { message: 'El patrón no puede subir de carpeta' }),
    }),
});
//# sourceMappingURL=tenancy.js.map